// =====================================================
// ADMINISTRACIÓN DE PARÁMETROS AGROPECUARIOS
// src/pages/Agro/Administracion/index.js
// =====================================================
// ACTUALIZADO: Sin datos de ejemplo, todo desde backend.
// Nuevo tab: Proyección Crediticia (config_proyeccion)
// =====================================================

import React, { useState, useEffect, useMemo } from "react"
import { formatearNumero } from "helpers/formatters"
import { getErrorMessage } from "helpers/error_helpers"
import {
  getCampanaSchema,
  CODIGO_CAMPANA_REGEX,
  MENSAJES_CAMPANA,
} from "./campanaSchema"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap"
import DataTable from "react-data-table-component"
import { opcionesPaginacion } from "components/Tables/paginacion"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import classnames from "classnames"
import * as XLSX from "xlsx"

import Layout from "components/Layout"
import NumberInput from "components/Input/NumberInput"
import AmountInput from "components/Input/AmountInput"
import toastHandler from "toastHandler"
// BDSJEA-136: Precios de Mercado como solapa de Administracion
import { PreciosMercadoPanel } from "pages/Agro/PreciosMercado"
// BDSJEA-166: administracion de procesos programados
import ProcesosPanel from "pages/Agro/Procesos"

import {
  getZonasAgro,
  saveZonaAgro,
  deleteZonaAgro,
  getCultivos,
  saveCultivo,
  deleteCultivo,
  getConceptosPlan,
  saveConceptoPlan,
  deleteConceptoPlan,
  getValoresAgro,
  saveValorAgro,
  deleteValorAgro,
  getCalendariosAgricolas,
  getImportacionesRendimientoOficial,
  getResumenRendimientoOficial,
  importarRendimientoOficial,
  getSerieRendimientoOficial,
  getOpcionesRendimientoOficial,
  exportarSerieRendimientoOficial,
  saveCalendarioAgricola,
  deleteCalendarioAgricola,
  getValoresGanaderia,
  saveValorGanaderia,
  deleteValorGanaderia,
  getEspeciesGanaderas,
  getCategoriasGanaderas,
  getValoresGenerales,
  saveValorGeneral,
  deleteValorGeneral,
  getConfigProyeccionLista,
  updateConfigProyeccion,
  sincronizarPrecios,
  actualizarCotizacionBcra,
  getCampanas,
  saveCampana,
} from "api/agro"

// =====================================================
// SCHEMAS DE VALIDACIÓN
// =====================================================
const ZonaSchema = Yup.object().shape({
  nombre: Yup.string()
    .required("Nombre requerido")
    .min(3, "Mínimo 3 caracteres"),
})

const CultivoSchema = Yup.object().shape({
  nombre: Yup.string().required("Nombre requerido"),
  codigo: Yup.string()
    .required("Código requerido")
    .max(10, "Máximo 10 caracteres"),
})

const ConceptoSchema = Yup.object().shape({
  concepto: Yup.string().required("Concepto requerido"),
  unidad: Yup.string().required("Unidad requerida"),
})

// BDSJEA-76: costos_total es una columna CALCULADA PERSISTIDA en la base
//   costo_directo + gastos_comercializacion + arrendamientos
//   + administracion_siembra + seguro_granizo
// SQL Server no permite escribirla (error 271) y el backend descarta
// costosTotal del payload a proposito. Se replica el calculo aca solo para
// mostrar el total en vivo mientras se edita; la fuente de verdad es la base.
const PARTIDAS_COSTO = [
  "costoDirecto",
  "gastosComercializacion",
  "arrendamientos",
  "administracionSiembra",
  "seguroGranizo",
]

const calcularCostosTotales = valores =>
  PARTIDAS_COSTO.reduce((acumulado, campo) => {
    const valor = parseFloat(valores?.[campo])
    return acumulado + (isNaN(valor) ? 0 : valor)
  }, 0)

const ValorAgroSchema = Yup.object().shape({
  idZona: Yup.number().required("Zona requerida"),
  idCultivo: Yup.number().required("Cultivo requerido"),
  rendimiento: Yup.number().min(0, "Debe ser positivo"),
  precio: Yup.number().min(0, "Debe ser positivo"),
})

const ValorGanaderiaSchema = Yup.object().shape({
  codigoEspecie: Yup.string().required("Especie requerida"),
  codigoCategoria: Yup.string().required("Categoría requerida"),
  pesoVenta: Yup.number().min(0).required("Peso requerido"),
  precioKilo: Yup.number().min(0).required("Precio requerido"),
})

// BDSJEA-143: alta y edicion del calendario agricola. Los meses y los dias de
// ciclo alimentan la fecha de cosecha estimada y el estado fenologico, asi que
// se validan en el formulario y ademas en el backend.
const CalendarioAgricolaSchema = Yup.object().shape({
  codigoCultivo: Yup.string().required("Código de cultivo requerido"),
  idZona: Yup.number().required("Zona requerida"),
  mesSiembraInicio: Yup.number().min(1).max(12).required("Requerido"),
  mesSiembraFin: Yup.number().min(1).max(12).required("Requerido"),
  mesCosechaInicio: Yup.number().min(1).max(12).required("Requerido"),
  mesCosechaFin: Yup.number().min(1).max(12).required("Requerido"),
  diasCicloMinimo: Yup.number().min(1).required("Requerido"),
  diasCicloMaximo: Yup.number().min(1).required("Requerido"),
  diasCicloTipico: Yup.number().min(1).required("Requerido"),
})

const ValorGeneralSchema = Yup.object().shape({
  concepto: Yup.string().required("Concepto requerido"),
  valor: Yup.number().required("Valor requerido"),
  unidad: Yup.string().required("Unidad requerida"),
})

// BDSJEA-143: nombres de mes para las ventanas de siembra y cosecha del
// calendario agrícola. El backend guarda el número de mes.
const MESES_CALENDARIO = [
  { valor: 1, nombre: "Enero", corto: "Ene" },
  { valor: 2, nombre: "Febrero", corto: "Feb" },
  { valor: 3, nombre: "Marzo", corto: "Mar" },
  { valor: 4, nombre: "Abril", corto: "Abr" },
  { valor: 5, nombre: "Mayo", corto: "May" },
  { valor: 6, nombre: "Junio", corto: "Jun" },
  { valor: 7, nombre: "Julio", corto: "Jul" },
  { valor: 8, nombre: "Agosto", corto: "Ago" },
  { valor: 9, nombre: "Septiembre", corto: "Sep" },
  { valor: 10, nombre: "Octubre", corto: "Oct" },
  { valor: 11, nombre: "Noviembre", corto: "Nov" },
  { valor: 12, nombre: "Diciembre", corto: "Dic" },
]

const nombreMesCorto = mes => {
  const encontrado = MESES_CALENDARIO.find(m => m.valor === Number(mes))
  return encontrado ? encontrado.corto : "—"
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================
const AdministracionAgropecuario = () => {
  const permissionBy = "agro.alta"
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("zonas")

  // Estados de datos
  const [zonas, setZonas] = useState([])
  const [cultivos, setCultivos] = useState([])
  const [conceptosPlan, setConceptosPlan] = useState([])
  const [valoresAgro, setValoresAgro] = useState([])
  const [valoresGanaderia, setValoresGanaderia] = useState([])
  const [especiesGanaderas, setEspeciesGanaderas] = useState([])
  const [categoriasGanaderas, setCategoriasGanaderas] = useState([])
  const [valoresGenerales, setValoresGenerales] = useState([])
  const [calendarios, setCalendarios] = useState([])
  // BDSJEA-153: serie oficial de rendimiento del Ministerio de Agricultura
  const [importaciones, setImportaciones] = useState([])
  const [resumenOficial, setResumenOficial] = useState([])
  const [archivoOficial, setArchivoOficial] = useState(null)
  const [importando, setImportando] = useState(false)
  const [serieOficial, setSerieOficial] = useState([])
  const [opcionesOficial, setOpcionesOficial] = useState({
    cultivos: [],
    campanias: [],
    provincias: [],
  })
  const [filtrosValoresAgro, setFiltrosValoresAgro] = useState({
    zona: "",
    cultivo: "",
  })
  const [filtrosOficial, setFiltrosOficial] = useState({
    cultivo: "",
    provincia: "",
    departamento: "",
    campania: "",
  })
  const [buscandoSerie, setBuscandoSerie] = useState(false)
  // BDSJEA-153: el paginado es del lado del servidor. La serie completa son
  // decenas de miles de filas y traerla entera no tiene sentido.
  const [totalSerie, setTotalSerie] = useState(0)
  const [paginaSerie, setPaginaSerie] = useState(1)
  const [tamanioSerie, setTamanioSerie] = useState(25)
  const [configProyeccion, setConfigProyeccion] = useState([])
  const [campanas, setCampanas] = useState([])

  // Modales
  const [modalZona, setModalZona] = useState(false)
  const [modalCultivo, setModalCultivo] = useState(false)
  const [modalConcepto, setModalConcepto] = useState(false)
  const [modalValorAgro, setModalValorAgro] = useState(false)
  const [modalValorGanaderia, setModalValorGanaderia] = useState(false)
  const [modalValorGeneral, setModalValorGeneral] = useState(false)
  const [modalCalendario, setModalCalendario] = useState(false)
  const [modalCampana, setModalCampana] = useState(false)
  // BDSJEA-165: subpestania activa dentro de Rendimiento Oficial.
  const [subTabOficial, setSubTabOficial] = useState("consulta")
  const [itemEdicion, setItemEdicion] = useState(null)

  const setSuccess = toastHandler(state => state.setSuccess)
  const setError = toastHandler(state => state.setError)

  const [sincronizando, setSincronizando] = useState(false)
  const [actualizandoCotizacion, setActualizandoCotizacion] = useState(false)

  // =====================================================
  // CARGA DE DATOS (TODO DESDE BACKEND)
  // =====================================================
  useEffect(() => {
    cargarDatos()
  }, [])

  const cargarDatos = async () => {
    setLoading(true)
    try {
      const [
        zonasData,
        cultivosData,
        conceptosData,
        vaData,
        vgData,
        especiesData,
        categoriasData,
        vgenData,
        calendariosData,
        importacionesData,
        resumenOficialData,
        opcionesOficialData,
        configData,
        campanasData,
      ] = await Promise.all([
        getZonasAgro(),
        getCultivos(),
        getConceptosPlan(),
        getValoresAgro(),
        getValoresGanaderia(),
        getEspeciesGanaderas().catch(() => []),
        getCategoriasGanaderas().catch(() => []),
        getValoresGenerales(),
        getCalendariosAgricolas().catch(() => []),
        getImportacionesRendimientoOficial().catch(() => []),
        getResumenRendimientoOficial().catch(() => []),
        getOpcionesRendimientoOficial().catch(() => ({
          cultivos: [],
          campanias: [],
          provincias: [],
        })),
        getConfigProyeccionLista().catch(() => []),
        getCampanas().catch(() => []),
      ])

      setZonas(zonasData)
      setCultivos(cultivosData)
      setConceptosPlan(conceptosData)
      setValoresAgro(vaData)
      setValoresGanaderia(vgData)
      setEspeciesGanaderas(especiesData)
      setCategoriasGanaderas(categoriasData)
      setValoresGenerales(vgenData)
      setCalendarios(calendariosData)
      setImportaciones(importacionesData)
      setResumenOficial(resumenOficialData)
      setOpcionesOficial(opcionesOficialData)
      setConfigProyeccion(configData)
      setCampanas(campanasData)
    } catch (error) {
      setError("Error al cargar datos: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  // =====================================================
  // HANDLERS - Guardan en backend y recargan
  // =====================================================
  const handleGuardarZona = async (values, { setSubmitting, resetForm }) => {
    try {
      await saveZonaAgro(itemEdicion ? { ...itemEdicion, ...values } : values)
      setSuccess("Zona guardada correctamente")
      setModalZona(false)
      setItemEdicion(null)
      resetForm()
      const data = await getZonasAgro()
      setZonas(data)
    } catch (error) {
      setError(
        "Error al guardar zona: " +
          (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleGuardarCultivo = async (values, { setSubmitting, resetForm }) => {
    try {
      await saveCultivo(itemEdicion ? { ...itemEdicion, ...values } : values)
      setSuccess("Cultivo guardado correctamente")
      setModalCultivo(false)
      setItemEdicion(null)
      resetForm()
      const data = await getCultivos()
      setCultivos(data)
    } catch (error) {
      setError(
        "Error al guardar cultivo: " +
          (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleGuardarConcepto = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      await saveConceptoPlan(
        itemEdicion ? { ...itemEdicion, ...values } : values,
      )
      setSuccess("Concepto guardado correctamente")
      setModalConcepto(false)
      setItemEdicion(null)
      resetForm()
      const data = await getConceptosPlan()
      setConceptosPlan(data)
    } catch (error) {
      setError(
        "Error al guardar concepto: " +
          (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleGuardarValorAgro = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      await saveValorAgro(itemEdicion ? { ...itemEdicion, ...values } : values)
      setSuccess("Valor agrícola guardado correctamente")
      setModalValorAgro(false)
      setItemEdicion(null)
      resetForm()
      const data = await getValoresAgro()
      setValoresAgro(data)
    } catch (error) {
      setError(
        "Error al guardar: " + (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  // BDSJEA-153: la serie tiene 55 campañas por cultivo y departamento, así que
  // se consulta con filtros y no se trae entera.
  const buscarSerieOficial = async (pagina = 1, tamanio = tamanioSerie) => {
    setBuscandoSerie(true)
    try {
      const filtros = Object.fromEntries(
        Object.entries(filtrosOficial).filter(([, v]) => v),
      )
      const r = await getSerieRendimientoOficial({
        ...filtros,
        pagina: pagina - 1,
        tamanio,
      })
      setSerieOficial(r.filas || [])
      setTotalSerie(r.total || 0)
      setPaginaSerie(pagina)
    } catch (error) {
      setError("No se pudo consultar la serie: " + error.message)
    } finally {
      setBuscandoSerie(false)
    }
  }

  // Un filtro nuevo vuelve a la primera página: seguir en la 8 con otro
  // conjunto de resultados es desorientador.
  const handleBuscarSerieOficial = () => buscarSerieOficial(1)

  const handleImportarRendimientoOficial = async () => {
    if (!archivoOficial) {
      setError(
        "Seleccioná el archivo descargado del Ministerio antes de importar.",
      )
      return
    }
    setImportando(true)
    try {
      const r = await importarRendimientoOficial(archivoOficial)
      setSuccess(r.mensaje || "Importación finalizada")
      setArchivoOficial(null)
      setImportaciones(await getImportacionesRendimientoOficial())
      setResumenOficial(await getResumenRendimientoOficial())
      setOpcionesOficial(await getOpcionesRendimientoOficial())
    } catch (error) {
      setError(
        "No se pudo importar: " +
          (error?.response?.data?.error ||
            error?.response?.data?.mensaje ||
            error.message),
      )
    } finally {
      setImportando(false)
    }
  }

  const handleGuardarCampana = async (values, { setSubmitting, resetForm }) => {
    try {
      await saveCampana(itemEdicion ? { ...itemEdicion, ...values } : values)
      setSuccess("Campaña guardada correctamente")
      setModalCampana(false)
      setItemEdicion(null)
      resetForm()
      setCampanas(await getCampanas())
    } catch (error) {
      // BDSJEA-447: el backend responde 400 con el motivo exacto (formato del código, fechas)
      setError(getErrorMessage(error, { prefix: "Error al guardar" }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleGuardarCalendario = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      await saveCalendarioAgricola(
        itemEdicion ? { ...itemEdicion, ...values } : values,
      )
      setSuccess("Calendario agrícola guardado correctamente")
      setModalCalendario(false)
      setItemEdicion(null)
      resetForm()
      setCalendarios(await getCalendariosAgricolas())
    } catch (error) {
      setError(
        "Error al guardar: " + (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleGuardarValorGanaderia = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      await saveValorGanaderia(
        itemEdicion ? { ...itemEdicion, ...values } : values,
      )
      setSuccess("Valor ganadería guardado correctamente")
      setModalValorGanaderia(false)
      setItemEdicion(null)
      resetForm()
      const data = await getValoresGanaderia()
      setValoresGanaderia(data)
    } catch (error) {
      setError(
        "Error al guardar: " + (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleGuardarValorGeneral = async (
    values,
    { setSubmitting, resetForm },
  ) => {
    try {
      await saveValorGeneral(
        itemEdicion ? { ...itemEdicion, ...values } : values,
      )
      setSuccess("Valor general guardado correctamente")
      setModalValorGeneral(false)
      setItemEdicion(null)
      resetForm()
      const data = await getValoresGenerales()
      setValoresGenerales(data)
    } catch (error) {
      setError(
        "Error al guardar: " + (error?.response?.data?.error || error.message),
      )
    } finally {
      setSubmitting(false)
    }
  }

  // =====================================================
  // HANDLERS DE ELIMINACIÓN
  // =====================================================
  const handleEliminar = async (tipo, id) => {
    if (!window.confirm("¿Está seguro que desea eliminar este elemento?"))
      return
    try {
      switch (tipo) {
        case "zona":
          await deleteZonaAgro(id)
          setZonas(await getZonasAgro())
          break
        case "cultivo":
          await deleteCultivo(id)
          setCultivos(await getCultivos())
          break
        case "concepto":
          await deleteConceptoPlan(id)
          setConceptosPlan(await getConceptosPlan())
          break
        case "valorAgro":
          await deleteValorAgro(id)
          setValoresAgro(await getValoresAgro())
          break
        case "valorGanaderia":
          await deleteValorGanaderia(id)
          setValoresGanaderia(await getValoresGanaderia())
          break
        case "valorGeneral":
          await deleteValorGeneral(id)
          setValoresGenerales(await getValoresGenerales())
          break
        case "calendario":
          await deleteCalendarioAgricola(id)
          setCalendarios(await getCalendariosAgricolas())
          break
      }
      setSuccess("Elemento eliminado correctamente")
    } catch (error) {
      setError(
        "Error al eliminar: " + (error?.response?.data?.error || error.message),
      )
    }
  }

  const handleEditar = (tipo, item) => {
    setItemEdicion(item)
    switch (tipo) {
      case "zona":
        setModalZona(true)
        break
      case "cultivo":
        setModalCultivo(true)
        break
      case "concepto":
        setModalConcepto(true)
        break
      case "valorAgro":
        setModalValorAgro(true)
        break
      case "valorGanaderia":
        setModalValorGanaderia(true)
        break
      case "valorGeneral":
        setModalValorGeneral(true)
        break
      case "calendario":
        setModalCalendario(true)
        break
      case "campana":
        setModalCampana(true)
        break
    }
  }

  const handleSincronizarPrecios = async () => {
    setSincronizando(true)
    try {
      const result = await sincronizarPrecios()
      setSuccess(
        `Precios actualizados: ${result.granosActualizados} granos, ${result.haciendaActualizada} hacienda`,
      )
      cargarDatos() // Recargar las tablas para ver los valores nuevos
    } catch (e) {
      setError(
        "Error al sincronizar: " + (e?.response?.data?.error || e.message),
      )
    } finally {
      setSincronizando(false)
    }
  }

  const handleActualizarCotizacionBcra = async () => {
    setActualizandoCotizacion(true)
    try {
      const result = await actualizarCotizacionBcra()
      setSuccess(result.mensaje || "Cotización BCRA actualizada correctamente")
      cargarDatos() // Recargar valores generales para ver el nuevo tipo de cambio
    } catch (e) {
      setError(
        "Error al actualizar cotización: " +
          (e?.response?.data?.error || e.message),
      )
    } finally {
      setActualizandoCotizacion(false)
    }
  }

  // =====================================================
  // EXPORTAR EXCEL
  // =====================================================
  const handleExportarExcel = async () => {
    // BDSJEA-153: la serie no está en memoria porque la grilla pagina contra el
    // servidor, así que se pide con los filtros elegidos antes de armar el libro.
    let serieParaExportar = []
    let avisoTruncado = ""
    try {
      const filtros = Object.fromEntries(
        Object.entries(filtrosOficial).filter(([, v]) => v),
      )
      const r = await exportarSerieRendimientoOficial(filtros)
      serieParaExportar = r.filas || []
      if (r.truncado) {
        avisoTruncado =
          ` La serie se exportó recortada a ${formatearNumero(r.limite, 0)} filas de ` +
          `${formatearNumero(r.total, 0)}: filtrá por cultivo o campaña para llevarte todo.`
      }
    } catch (error) {
      setError(
        "No se pudo exportar la serie de rendimiento oficial: " + error.message,
      )
      return
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(zonas), "Zonas")
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(cultivos),
      "Cultivos",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(valoresAgro),
      "Valores Agricolas",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(valoresGanaderia),
      "Valores Ganaderia",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(valoresGenerales),
      "Valores Generales",
    )
    // BDSJEA-153: faltaban tres solapas. Conceptos Plan y Calendario Agrícola se
    // exportan completos porque la pantalla ya los tiene en memoria. De
    // Rendimiento Oficial van el resumen y el historial de importaciones, no la
    // serie: son cientos de miles de filas paginadas desde el servidor y armar
    // ese xlsx en el navegador lo colgaría.
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(conceptosPlan),
      "Conceptos Plan",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(calendarios),
      "Calendario Agrícola",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(resumenOficial),
      "Rend Oficial - Resumen",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(serieParaExportar),
      "Rend Oficial - Datos",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(importaciones),
      "Rend Oficial - Importaciones",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(configProyeccion),
      "Config Proyeccion",
    )
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(campanas),
      "Campañas",
    )
    XLSX.writeFile(wb, "Parametros_Agropecuarios.xlsx")
    setSuccess("Excel exportado correctamente." + avisoTruncado)
  }

  // =====================================================
  // COLUMNAS DE TABLAS (sin cambios respecto al original)
  // =====================================================
  const columnasZonas = [
    { name: "Zona", selector: row => row.nombre, sortable: true, grow: 2 },
    {
      // BDSJEA-149: zona cuyas fechas de siembra y cosecha se usan cuando la
      // zona del establecimiento no tiene calendario propio. Solo puede haber
      // una.
      name: "Referencia calendario",
      cell: row =>
        row.esReferenciaCalendario === true ||
        row.esReferenciaCalendario === 1 ||
        row.es_referencia_calendario === true ||
        row.es_referencia_calendario === 1 ? (
          <Badge color="info">Referencia</Badge>
        ) : (
          ""
        ),
      width: "180px",
      center: true,
    },
    {
      name: "Estado",
      cell: row => (
        <Badge color={row.activa ? "success" : "secondary"}>
          {row.activa ? "Activa" : "Inactiva"}
        </Badge>
      ),
      width: "100px",
    },
    {
      name: "Acciones",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("zona", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("zona", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "100px",
      center: true,
    },
  ]

  const columnasCultivos = [
    { name: "Cultivo", selector: row => row.nombre, sortable: true, grow: 2 },
    { name: "Código", selector: row => row.codigo, width: "120px" },
    { name: "Tipo", selector: row => row.tipo, width: "150px" },
    // BDSJEA-149: los días de ciclo se muestran en Calendario Agrícola, que los
    // tiene por cultivo, variante y zona.
    {
      // BDSJEA-153: sin este nombre el cultivo no se importa de la serie oficial.
      name: "Nombre en MAGyP",
      selector: row => row.nombreMagyp || "—",
      grow: 2,
    },
    {
      name: "Estado",
      cell: row => (
        <Badge color={row.activo ? "success" : "secondary"}>
          {row.activo ? "Activo" : "Inactivo"}
        </Badge>
      ),
      width: "100px",
    },
    {
      name: "Acciones",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("cultivo", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("cultivo", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "100px",
      center: true,
    },
  ]

  const columnasConceptos = [
    {
      name: "Concepto",
      selector: row => row.concepto,
      sortable: true,
      grow: 2,
    },
    { name: "Unidad", selector: row => row.unidad, width: "120px" },
    {
      name: "Estado",
      cell: row => (
        <Badge color={row.activo ? "success" : "secondary"}>
          {row.activo ? "Activo" : "Inactivo"}
        </Badge>
      ),
      width: "100px",
    },
    {
      name: "Acciones",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("concepto", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("concepto", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "100px",
      center: true,
    },
  ]

  const columnasValoresAgro = [
    { name: "Zona", selector: row => row.zona, sortable: true, width: "250px" },
    {
      name: "Cultivo",
      selector: row => row.cultivo,
      sortable: true,
      width: "100px",
    },
    {
      // BDSJEA-165: el rendimiento se podia editar en el modal pero no se veia
      // en la grilla. Con la resolucion de zona por departamento este valor pasa
      // a usarse en mas casos, asi que tiene que ser revisable de un vistazo.
      name: (
        <>
          Rendimiento
          <br />
          (qq/ha)
        </>
      ),
      selector: row =>
        row.rendimiento != null ? formatearNumero(row.rendimiento, 2) : "",
      sortable: true,
      right: true,
      width: "120px",
    },
    {
      name: (
        <>
          Precio
          <br />
          (USD/tn)
        </>
      ),
      selector: row =>
        row.precio != null ? formatearNumero(row.precio, 2) : "",
      right: true,
      width: "100px",
    },
    {
      name: (
        <>
          Gastos Comerciales
          <br />
          (USD)
        </>
      ),
      selector: row =>
        row.gastosComercializacion != null
          ? formatearNumero(row.gastosComercializacion, 2)
          : "",
      right: true,
      width: "130px",
    },
    {
      name: (
        <>
          Arrendamiento
          <br />
          (USD)
        </>
      ),
      selector: row =>
        row.arrendamientos != null
          ? formatearNumero(row.arrendamientos, 2)
          : "",
      right: true,
      width: "130px",
    },
    {
      // BDSJEA-76: esta columna mostraba costosTotal bajo la etiqueta "Costos".
      // El dato editable es el costo directo; el total va aparte, al final.
      name: (
        <>
          Costo Directo
          <br />
          (USD)
        </>
      ),
      selector: row =>
        row.costoDirecto != null ? formatearNumero(row.costoDirecto, 2) : "",
      right: true,
      width: "130px",
    },
    {
      name: (
        <>
          Admin.
          <br />
          (USD)
        </>
      ),
      selector: row =>
        row.administracionSiembra != null
          ? formatearNumero(row.administracionSiembra, 2)
          : "",
      right: true,
      width: "100px",
    },
    {
      name: (
        <>
          Seguro
          <br />
          (USD)
        </>
      ),
      selector: row =>
        row.seguroGranizo != null ? formatearNumero(row.seguroGranizo, 2) : "",
      right: true,
      width: "100px",
    },
    {
      // BDSJEA-76: suma de las cinco partidas, calculada en la base. Es el
      // numero que consume la proyeccion crediticia y el scoring, asi que
      // conviene tenerlo a la vista aunque no sea editable.
      name: (
        <>
          Costos Totales
          <br />
          (USD)
        </>
      ),
      selector: row =>
        row.costosTotal != null ? formatearNumero(row.costosTotal, 2) : "",
      right: true,
      width: "130px",
    },
    {
      name: "",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("valorAgro", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("valorAgro", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "80px",
      center: true,
    },
  ]

  // BDSJEA-143: MESES se usa para mostrar las ventanas de siembra y cosecha en
  // texto en vez del número de mes.
  const columnasSerieOficial = [
    {
      name: "Campaña",
      selector: row => row.campania,
      sortable: true,
      width: "110px",
    },
    {
      name: "Cultivo",
      selector: row => row.nombreCultivo,
      sortable: true,
      grow: 2,
    },
    {
      name: "Provincia",
      selector: row => row.provincia,
      sortable: true,
      grow: 2,
    },
    {
      name: "Departamento",
      selector: row => row.departamento || "Total provincial",
      sortable: true,
      grow: 2,
    },
    {
      name: (
        <>
          Sembrada
          <br />
          (ha)
        </>
      ),
      selector: row =>
        row.superficieSembrada != null
          ? formatearNumero(row.superficieSembrada, 0)
          : "",
      right: true,
      width: "110px",
    },
    {
      name: (
        <>
          Cosechada
          <br />
          (ha)
        </>
      ),
      selector: row =>
        row.superficieCosechada != null
          ? formatearNumero(row.superficieCosechada, 0)
          : "",
      right: true,
      width: "110px",
    },
    {
      name: (
        <>
          Producción
          <br />
          (tn)
        </>
      ),
      selector: row =>
        row.produccion != null ? formatearNumero(row.produccion, 0) : "",
      right: true,
      width: "110px",
    },
    {
      name: (
        <>
          Rendimiento
          <br />
          (qq/ha)
        </>
      ),
      selector: row =>
        row.rendimientoQqHa != null
          ? formatearNumero(row.rendimientoQqHa, 2)
          : "",
      right: true,
      width: "120px",
    },
    {
      name: "Estado",
      cell: row =>
        row.provisorio ? (
          <Badge color="warning" title="La fuente la publica como provisoria">
            Provisoria
          </Badge>
        ) : (
          <Badge color="light" className="text-muted">
            Definitiva
          </Badge>
        ),
      width: "120px",
      center: true,
    },
  ]

  const columnasResumenOficial = [
    {
      name: "Cultivo",
      selector: row => row.nombreCultivo,
      sortable: true,
      grow: 2,
    },
    {
      name: "Campañas",
      selector: row => row.campanias,
      right: true,
      width: "110px",
    },
    {
      name: "Desde",
      selector: row => row.campaniaDesde,
      width: "110px",
    },
    {
      name: "Hasta",
      selector: row => row.campaniaHasta,
      width: "110px",
    },
    {
      name: "Departamentos",
      selector: row => row.departamentos,
      right: true,
      width: "140px",
    },
    {
      name: "Filas",
      selector: row => row.filas,
      right: true,
      width: "100px",
    },
  ]

  const columnasImportaciones = [
    {
      name: "Fecha",
      selector: row =>
        row.fecha_importacion
          ? new Date(row.fecha_importacion).toLocaleString("es-AR")
          : "",
      width: "170px",
    },
    {
      name: "Archivo",
      selector: row => row.nombre_archivo || "—",
      grow: 2,
    },
    {
      name: "Estado",
      cell: row => (
        <Badge
          color={
            row.estado === "OK"
              ? "success"
              : row.estado === "ERROR"
                ? "danger"
                : "warning"
          }
        >
          {row.estado}
        </Badge>
      ),
      width: "150px",
      center: true,
    },
    {
      name: "Filas",
      selector: row =>
        `${row.filas_importadas || 0} / ${row.filas_leidas || 0}`,
      right: true,
      width: "110px",
    },
    {
      name: "Detalle",
      selector: row => row.mensaje || "",
      grow: 3,
      wrap: true,
    },
    {
      name: "Usuario",
      selector: row => row.usuario || "—",
      width: "140px",
    },
  ]

  const columnasCalendario = [
    {
      name: "Cultivo",
      selector: row => row.nombreCultivo,
      sortable: true,
      grow: 2,
    },
    {
      name: "Variante",
      selector: row => row.variante || "—",
      sortable: true,
      width: "110px",
    },
    { name: "Zona", selector: row => row.zona, sortable: true, grow: 2 },
    {
      name: "Siembra",
      selector: row =>
        `${nombreMesCorto(row.mesSiembraInicio)} a ${nombreMesCorto(
          row.mesSiembraFin,
        )}`,
      width: "130px",
    },
    {
      name: "Cosecha",
      selector: row =>
        `${nombreMesCorto(row.mesCosechaInicio)} a ${nombreMesCorto(
          row.mesCosechaFin,
        )}`,
      width: "130px",
    },
    {
      name: (
        <>
          Ciclo
          <br />
          (días)
        </>
      ),
      selector: row => row.diasCicloTipico,
      right: true,
      width: "90px",
    },
    {
      name: "Fuente",
      selector: row => row.fuenteDatos || "—",
      grow: 2,
    },
    {
      name: "Estado",
      cell: row =>
        row.activo === false ? (
          <Badge color="secondary">Inactivo</Badge>
        ) : (
          <Badge color="success">Activo</Badge>
        ),
      width: "100px",
      center: true,
    },
    {
      name: "",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("calendario", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("calendario", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "80px",
      center: true,
    },
  ]

  const valoresAgroFiltrados = valoresAgro.filter(va => {
    if (filtrosValoresAgro.zona && va.zona !== filtrosValoresAgro.zona)
      return false
    if (filtrosValoresAgro.cultivo && va.cultivo !== filtrosValoresAgro.cultivo)
      return false
    return true
  })

  const columnasValoresGanaderia = [
    {
      name: "Especie",
      selector: row => row.codigoEspecie,
      cell: row =>
        especiesGanaderas.find(e => e.codigo === row.codigoEspecie)
          ?.descripcion || row.codigoEspecie,
      sortable: true,
      width: "140px",
    },
    {
      name: "Categoría",
      selector: row => row.codigoCategoria,
      cell: row => (
        <span>
          {row.tipoHacienda || row.codigoCategoria}
          {row.codigoCategoria && (
            <small className="text-muted d-block">{row.codigoCategoria}</small>
          )}
        </span>
      ),
      sortable: true,
      width: "200px",
      grow: 2,
    },
    {
      name: "Peso Promedio (kg)",
      selector: row => row.pesoVenta,
      right: true,
      width: "180px",
    },
    {
      name: "Precio/kg (USD)",
      selector: row =>
        row.precioKilo != null ? formatearNumero(row.precioKilo, 2) : "",
      right: true,
      width: "180px",
    },
    {
      name: "Gastos Venta %",
      selector: row => (row.gastosVenta != null ? `${row.gastosVenta}%` : ""),
      right: true,
      width: "180px",
    },
    {
      name: "Gastos Productivos/Cabeza (USD)",
      selector: row =>
        row.gastosProductivos != null
          ? formatearNumero(row.gastosProductivos, 0)
          : "",
      right: true,
      width: "260px",
    },
    {
      name: "Gastos Cría/cabeza (USD)",
      selector: row =>
        row.gastosCria != null ? formatearNumero(row.gastosCria, 0) : "",
      right: true,
      width: "260px",
    },
    {
      name: "Últ. sincronización",
      selector: row => row.fechaModificacion,
      sortable: true,
      width: "160px",
      cell: row =>
        row.fechaModificacion ? (
          <small className="text-muted">
            {new Date(row.fechaModificacion).toLocaleString("es-AR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </small>
        ) : (
          <Badge color="secondary">Sin dato</Badge>
        ),
    },
    {
      name: "",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("valorGanaderia", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("valorGanaderia", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "80px",
      center: true,
    },
  ]

  const columnasValoresGenerales = [
    {
      name: "Concepto",
      selector: row => row.concepto,
      sortable: true,
      grow: 2,
    },
    {
      name: "Valor",
      selector: row =>
        row.valor != null
          ? Number(row.valor).toLocaleString("es-AR", {
              maximumFractionDigits: 2,
            })
          : "",
      right: true,
      width: "120px",
    },
    { name: "Unidad", selector: row => row.unidad, width: "100px" },
    {
      name: "",
      cell: row => (
        <div className="d-flex gap-1">
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEditar("valorGeneral", row)}
          >
            <i className="mdi mdi-pencil text-primary" />
          </Button>
          <Button
            color="link"
            className="fs-5"
            onClick={() => handleEliminar("valorGeneral", row.id)}
          >
            <i className="mdi mdi-delete text-danger" />
          </Button>
        </div>
      ),
      width: "80px",
      center: true,
    },
  ]

  const columnasCampanas = [
    {
      name: "Código",
      selector: row => row.codigo,
      sortable: true,
      width: "120px",
    },
    { name: "Descripción", selector: row => row.descripcion || "—", grow: 2 },
    { name: "Inicio", selector: row => row.fechaInicio || "—", width: "130px" },
    { name: "Fin", selector: row => row.fechaFin || "—", width: "130px" },
    {
      name: "Estado",
      cell: row => (
        <Badge color={row.activa ? "success" : "secondary"}>
          {row.activa ? "Actual" : "Inactiva"}
        </Badge>
      ),
      width: "110px",
      center: true,
    },
    {
      name: "Acciones",
      cell: row => (
        <Button
          color="link"
          className="fs-5"
          onClick={() => handleEditar("campana", row)}
        >
          <i className="mdi mdi-pencil text-primary" />
        </Button>
      ),
      width: "80px",
      center: true,
    },
  ]

  // =====================================================
  // AGRUPACIÓN CONFIG PROYECCIÓN POR CATEGORÍA
  // =====================================================
  const configAgrupada = useMemo(
    () =>
      configProyeccion.reduce((acc, item) => {
        const cat = item.categoria || "OTROS"
        if (!acc[cat]) acc[cat] = []
        acc[cat].push(item)
        return acc
      }, {}),
    [configProyeccion],
  )

  const nombreCategoria = {
    CAPACIDAD_PAGO: "Capacidad de pago",
    NDVI: "Ajuste por NDVI",
    GANADERIA: "Ganadería",
    TAMBO: "Tambo",
    COSTOS: "Costos completos por cultivo",
    RENDIMIENTOS: "Rendimientos por cultivo",
    PRECIOS: "Precios de referencia por cultivo",
    FORRAJERAS: "Modelo forrajero perenne (alfalfa)",
    PLANTACIONES: "Plantaciones perennes (yerba, té, caña, frutales)",
    DICTAMEN: "Dictamen crediticio",
  }

  const formatearClave = clave =>
    clave
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase())
      .replace(/Usd/g, "USD")
      .replace(/Ha$/g, "/ha")
      .replace(/Ndvi/g, "NDVI")
      .replace(/Qq/g, "qq")

  // =====================================================
  // RENDER
  // =====================================================
  if (loading) {
    return (
      <Layout
        components={[
          {
            component: (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Cargando parámetros...</p>
              </div>
            ),
          },
        ]}
      />
    )
  }

  const tabs = [
    { id: "zonas", label: "Zonas", icon: "mdi-map-marker" },
    { id: "cultivos", label: "Cultivos", icon: "mdi-corn" },
    { id: "conceptos", label: "Conceptos Plan", icon: "mdi-calculator" },
    { id: "valoresAgro", label: "Valores Agro", icon: "mdi-currency-usd" },
    { id: "valoresGanaderia", label: "Valores Ganadería", icon: "mdi-cow" },
    {
      id: "valoresGenerales",
      label: "Valores Generales",
      icon: "mdi-cog-outline",
    },
    {
      id: "calendario",
      label: "Calendario Agrícola",
      icon: "mdi-calendar-month",
    },
    {
      id: "campanas",
      label: "Campañas",
      icon: "mdi-calendar-range",
    },
    {
      id: "rendimientoOficial",
      label: "Rendimiento Oficial",
      icon: "mdi-database-import",
    },
    {
      id: "configProyeccion",
      label: "Proyección Crediticia",
      icon: "mdi-trending-up",
    },
    // BDSJEA-136
    {
      id: "preciosMercado",
      label: "Precios Mercado",
      icon: "mdi-chart-line",
    },
    // BDSJEA-166
    {
      id: "procesos",
      label: "Procesos",
      icon: "mdi-cog-sync",
    },
  ]

  const breadcrumbItems = [
    { title: "Agropecuario", link: "/agro" },
    { title: "Administración", link: "#" },
  ]

  return (
    <Layout
      title="Administración"
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
      components={[
        {
          component: (
            <>
              <Card className="mb-3">
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div>
                      <h4 className="mb-1">
                        <i className="mdi mdi-cog me-2" />
                        Administración de Parámetros Agropecuarios
                      </h4>
                      <p className="text-muted mb-0">
                        Gestione las tablas de configuración del módulo
                        agropecuario
                      </p>
                    </div>
                    <div className="d-flex gap-2 mt-2 mt-md-0">
                      {/* BDSJEA-159: manual de parámetros. Es un PDF estático en
                          public/, generado desde public/manual-parametros-agro.py;
                          si cambian los parámetros hay que regenerarlo. */}
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        tag="a"
                        href={`${process.env.PUBLIC_URL || ""}/manual-parametros-agro.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="mdi mdi-book-open-page-variant me-1" />
                        Manual de parámetros
                      </Button>
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        onClick={handleExportarExcel}
                      >
                        <i className="mdi mdi-download me-1" />
                        Exportar Excel
                      </Button>
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={handleSincronizarPrecios}
                        disabled={sincronizando}
                      >
                        {sincronizando ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Sincronizando...
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-sync me-1" />
                            Sincronizar precios de mercado
                          </>
                        )}
                      </Button>
                      <Button
                        color="dark"
                        outline
                        size="sm"
                        onClick={handleActualizarCotizacionBcra}
                        disabled={actualizandoCotizacion}
                      >
                        {actualizandoCotizacion ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            Actualizando...
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-cash-usd me-1" />
                            Obtener cotización dólar
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <style>{`
            .nav-tabs-custom .nav-link.active { color: #495057 !important; background-color: #fff !important; font-weight: 600 !important; border-color: #dee2e6 #dee2e6 #fff !important; }
            .nav-tabs-custom .nav-link { color: #6c757d !important; cursor: pointer; }
            .nav-tabs-custom .nav-link:hover:not(.active) { color: #495057 !important; border-color: #e9ecef #e9ecef #dee2e6 !important; }
          `}</style>

                  <Nav tabs className="nav-tabs-custom mb-3">
                    {tabs.map(tab => (
                      <NavItem key={tab.id}>
                        <NavLink
                          className={classnames({
                            active: activeTab === tab.id,
                          })}
                          onClick={() => setActiveTab(tab.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <i className={`mdi ${tab.icon} me-1`} />
                          {tab.label}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <TabContent activeTab={activeTab}>
                    {/* Tabs existentes: Zonas, Cultivos, Conceptos, ValoresAgro, ValoresGanaderia, ValoresGenerales */}
                    <TabPane tabId="zonas">
                      <TabHeader
                        title="Zonas Agropecuarias"
                        icon="mdi-map-marker"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalZona(true)
                        }}
                      />
                      <DataTable
                        columns={columnasZonas}
                        data={zonas}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(zonas.length)}
                        highlightOnHover
                        noDataComponent="No hay zonas registradas"
                      />
                    </TabPane>

                    <TabPane tabId="cultivos">
                      <TabHeader
                        title="Cultivos"
                        icon="mdi-corn"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalCultivo(true)
                        }}
                      />
                      <DataTable
                        columns={columnasCultivos}
                        data={cultivos}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(cultivos.length)}
                        highlightOnHover
                        noDataComponent="No hay cultivos registrados"
                      />
                    </TabPane>

                    <TabPane tabId="conceptos">
                      <TabHeader
                        title="Conceptos Plan Productivo"
                        icon="mdi-calculator"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalConcepto(true)
                        }}
                      />
                      <DataTable
                        columns={columnasConceptos}
                        data={conceptosPlan}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(conceptosPlan.length)}
                        highlightOnHover
                        noDataComponent="No hay conceptos registrados"
                      />
                    </TabPane>

                    <TabPane tabId="valoresAgro">
                      <TabHeader
                        title="Valores de Agricultura (por Zona y Cultivo)"
                        icon="mdi-currency-usd"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalValorAgro(true)
                        }}
                      />
                      <Row className="mb-2 g-2">
                        <Col md={4}>
                          <Input
                            type="select"
                            value={filtrosValoresAgro.zona}
                            onChange={e =>
                              setFiltrosValoresAgro(f => ({
                                ...f,
                                zona: e.target.value,
                              }))
                            }
                          >
                            <option value="">Todas las zonas</option>
                            {[
                              ...new Set(
                                valoresAgro.map(va => va.zona).filter(Boolean),
                              ),
                            ]
                              .sort()
                              .map(z => (
                                <option key={z} value={z}>
                                  {z}
                                </option>
                              ))}
                          </Input>
                        </Col>
                        <Col md={3}>
                          <Input
                            type="select"
                            value={filtrosValoresAgro.cultivo}
                            onChange={e =>
                              setFiltrosValoresAgro(f => ({
                                ...f,
                                cultivo: e.target.value,
                              }))
                            }
                          >
                            <option value="">Todos los cultivos</option>
                            {[
                              ...new Set(
                                valoresAgro
                                  .map(va => va.cultivo)
                                  .filter(Boolean),
                              ),
                            ]
                              .sort()
                              .map(c => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                          </Input>
                        </Col>
                      </Row>
                      <DataTable
                        columns={columnasValoresAgro}
                        data={valoresAgroFiltrados}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(valoresAgroFiltrados.length)}
                        highlightOnHover
                        noDataComponent="No hay valores registrados"
                      />
                    </TabPane>

                    <TabPane tabId="valoresGanaderia">
                      <TabHeader
                        title="Valores de Ganadería (por Tipo de Hacienda)"
                        icon="mdi-cow"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalValorGanaderia(true)
                        }}
                      />
                      <Alert color="info" className="py-2 small">
                        <i className="mdi mdi-information-outline me-1" />
                        Los precios por kg (USD) se sincronizan automáticamente
                        desde <strong>
                          Mercado Agroganadero (ex-Liniers)
                        </strong>{" "}
                        los días hábiles a las 20:00 hs. Requiere{" "}
                        <em>Tipo de cambio USD</em> configurado en Valores
                        Generales. También puede actualizarse manualmente con el
                        botón <strong>Sincronizar precios de mercado</strong>.
                      </Alert>
                      <DataTable
                        columns={columnasValoresGanaderia}
                        data={valoresGanaderia}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(valoresGanaderia.length)}
                        highlightOnHover
                        noDataComponent="No hay valores registrados"
                      />
                    </TabPane>

                    <TabPane tabId="valoresGenerales">
                      <TabHeader
                        title="Valores Generales - Ganadería y Tambo"
                        icon="mdi-cog-outline"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalValorGeneral(true)
                        }}
                      />
                      <DataTable
                        columns={columnasValoresGenerales}
                        data={valoresGenerales}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(valoresGenerales.length)}
                        highlightOnHover
                        noDataComponent="No hay valores registrados"
                      />
                    </TabPane>

                    {/* NUEVO TAB: Config Proyección Crediticia con sub-tabs */}
                    <TabPane tabId="calendario">
                      <TabHeader
                        title="Calendario Agrícola (por Cultivo, Variante y Zona)"
                        icon="mdi-calendar-month"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalCalendario(true)
                        }}
                      />
                      <Alert color="info" className="py-2 small">
                        <i className="mdi mdi-information-outline me-1" />
                        Datos de referencia agronómica (fuente INTA y bolsas de
                        cereales). Definen las ventanas de siembra y cosecha y
                        el ciclo del cultivo. El rendimiento, el precio y los
                        costos de la zona se administran en Valores Agro: el
                        precio se actualiza solo todos los días hábiles.
                      </Alert>
                      <DataTable
                        columns={columnasCalendario}
                        data={calendarios}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(calendarios.length)}
                        highlightOnHover
                        noDataComponent="No hay calendarios registrados"
                      />
                    </TabPane>

                    <TabPane tabId="campanas">
                      <TabHeader
                        title="Campañas Agrícolas"
                        icon="mdi-calendar-range"
                        onAdd={() => {
                          setItemEdicion(null)
                          setModalCampana(true)
                        }}
                      />
                      <Alert color="info" className="py-2 small">
                        <i className="mdi mdi-information-outline me-1" />
                        Solo puede haber una campaña activa a la vez. Al activar
                        una nueva, la anterior se desactiva automáticamente.
                      </Alert>
                      <DataTable
                        columns={columnasCampanas}
                        data={campanas}
                        pagination
                        paginationPerPage={10}
                        {...opcionesPaginacion(campanas.length)}
                        highlightOnHover
                        noDataComponent="No hay campañas registradas"
                      />
                    </TabPane>

                    <TabPane tabId="rendimientoOficial">
                      <h5 className="mb-3">
                        <i className="mdi mdi-database-import me-2 text-primary" />
                        Rendimiento Oficial — Estimaciones Agrícolas (MAGyP)
                      </h5>

                      <Alert color="info" className="py-2 small mb-3">
                        <i className="mdi mdi-information-outline me-1" />
                        Serie oficial de rendimiento por cultivo, campaña y
                        departamento publicada por la Dirección de Estimaciones
                        Agrícolas. Se usa para estimar el rendimiento del
                        departamento del establecimiento y para el comparativo
                        del informe. No reemplaza los valores de Valores Agro,
                        que siguen siendo los parámetros del banco.
                      </Alert>

                      {/* BDSJEA-165: la solapa juntaba cuatro cosas distintas en
                          un mismo scroll: instrucciones de descarga, importacion,
                          consulta de la serie e historial. Se separan en
                          subpestanias. "Consultar serie" queda primero porque es
                          lo que se usa a diario; importar pasa una vez por
                          campania. */}
                      <Nav tabs className="mb-3">
                        {[
                          {
                            id: "consulta",
                            label: "Consultar serie",
                            icon: "mdi-table-search",
                          },
                          {
                            id: "importar",
                            label: "Importar",
                            icon: "mdi-upload",
                          },
                          {
                            id: "historial",
                            label: "Historial",
                            icon: "mdi-history",
                          },
                        ].map(sub => (
                          <NavItem key={sub.id}>
                            <NavLink
                              className={classnames("cursor-pointer", {
                                active: subTabOficial === sub.id,
                              })}
                              onClick={() => setSubTabOficial(sub.id)}
                            >
                              <i className={`mdi ${sub.icon} me-1`} />
                              {sub.label}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>

                      <TabContent activeTab={subTabOficial}>
                        <TabPane tabId="consulta">
                          <h6 className="mb-2 mt-4">Datos cargados</h6>
                          <Row className="mb-2">
                            <Col md={3}>
                              <Input
                                bsSize="sm"
                                type="select"
                                value={filtrosOficial.cultivo}
                                onChange={e =>
                                  setFiltrosOficial({
                                    ...filtrosOficial,
                                    cultivo: e.target.value,
                                  })
                                }
                              >
                                <option value="">Todos los cultivos</option>
                                {(opcionesOficial.cultivos || []).map(c => (
                                  <option key={c.codigo} value={c.codigo}>
                                    {c.nombre}
                                  </option>
                                ))}
                              </Input>
                            </Col>
                            <Col md={3}>
                              <Input
                                bsSize="sm"
                                type="select"
                                value={filtrosOficial.provincia}
                                onChange={e =>
                                  setFiltrosOficial({
                                    ...filtrosOficial,
                                    provincia: e.target.value,
                                  })
                                }
                              >
                                <option value="">Todas las provincias</option>
                                {(opcionesOficial.provincias || []).map(p => (
                                  <option key={p.nombre} value={p.nombre}>
                                    {p.nombre}
                                  </option>
                                ))}
                              </Input>
                            </Col>
                            <Col md={2}>
                              <Input
                                bsSize="sm"
                                type="select"
                                value={filtrosOficial.campania}
                                onChange={e =>
                                  setFiltrosOficial({
                                    ...filtrosOficial,
                                    campania: e.target.value,
                                  })
                                }
                              >
                                <option value="">Todas las campañas</option>
                                {(opcionesOficial.campanias || []).map(c => (
                                  <option key={c.campania} value={c.campania}>
                                    {c.campania}
                                  </option>
                                ))}
                              </Input>
                            </Col>
                            <Col md={2}>
                              <Input
                                bsSize="sm"
                                placeholder="Departamento"
                                value={filtrosOficial.departamento}
                                onChange={e =>
                                  setFiltrosOficial({
                                    ...filtrosOficial,
                                    departamento: e.target.value,
                                  })
                                }
                              />
                            </Col>
                            <Col md={2}>
                              <Button
                                size="sm"
                                color="primary"
                                disabled={buscandoSerie}
                                onClick={handleBuscarSerieOficial}
                              >
                                {buscandoSerie ? "Buscando..." : "Buscar"}
                              </Button>
                            </Col>
                          </Row>
                          <DataTable
                            columns={columnasSerieOficial}
                            data={serieOficial}
                            progressPending={buscandoSerie}
                            pagination
                            paginationServer
                            paginationTotalRows={totalSerie}
                            paginationDefaultPage={paginaSerie}
                            paginationPerPage={tamanioSerie}
                            paginationRowsPerPageOptions={[25, 50, 100, 200]}
                            onChangePage={pagina => buscarSerieOficial(pagina)}
                            onChangeRowsPerPage={(tamanio, pagina) => {
                              setTamanioSerie(tamanio)
                              buscarSerieOficial(pagina, tamanio)
                            }}
                            dense
                            highlightOnHover
                            noDataComponent="Elegí los filtros y presioná Buscar"
                          />
                          <div className="small text-muted mt-1 mb-4">
                            {totalSerie > 0
                              ? `${formatearNumero(totalSerie, 0)} filas para los filtros elegidos.`
                              : ""}{" "}
                            Las filas sin departamento son los totales
                            provinciales, que se usan cuando el establecimiento
                            no tiene departamento resuelto.
                          </div>
                        </TabPane>

                        <TabPane tabId="importar">
                          <Card className="mb-3">
                            <CardBody>
                              <h6 className="mb-3">Cómo obtener el archivo</h6>
                              <ol className="small mb-3 ps-3">
                                <li className="mb-1">
                                  Entrar a{" "}
                                  <a
                                    href="https://datosestimaciones.magyp.gob.ar/reportes.php?reporte=Estimaciones"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    datosestimaciones.magyp.gob.ar
                                  </a>
                                </li>
                                <li className="mb-1">
                                  En <strong>Cultivo</strong> elegir uno solo.
                                  Los nombres que el sistema reconoce son:{" "}
                                  <strong>
                                    Soja 1ra, Maíz, Trigo total, Girasol, Sorgo,
                                    Cebada cervecera y Algodón
                                  </strong>
                                  . Cualquier otro se ignora y queda listado al
                                  final de la importación.
                                </li>
                                <li className="mb-1">
                                  En <strong>Variable</strong> no filtrar: el
                                  archivo tiene que traer superficie sembrada,
                                  cosechada, producción y rendimiento.
                                </li>
                                <li className="mb-1">
                                  En <strong>Desde</strong> y{" "}
                                  <strong>Hasta</strong>
                                  elegir el rango de campañas. Con las últimas
                                  10 alcanza para el promedio y para el
                                  contraste con lo declarado por el productor.
                                </li>
                                <li className="mb-1">
                                  En <strong>Agregación</strong> elegir
                                  <strong> Total Departamentos</strong>. Es el
                                  punto más importante: con Total País o Total
                                  Provincia el archivo no trae la columna de
                                  departamento y el dato queda demasiado grueso
                                  para el análisis.
                                </li>
                                <li className="mb-1">
                                  Presionar <strong>Descargar</strong> y subir
                                  el archivo acá sin abrirlo ni convertirlo. Si
                                  se abre en Excel y se vuelve a guardar, cambia
                                  el formato y la importación falla.
                                </li>
                              </ol>
                              <div className="small text-muted mb-3">
                                Se puede repetir cultivo por cultivo. Cada
                                importación reemplaza lo que ya estaba cargado
                                para ese cultivo y esas campañas, así que volver
                                a subir un archivo corregido no duplica nada.
                              </div>

                              <Row className="align-items-end">
                                <Col md={7}>
                                  <FormGroup className="mb-0">
                                    <Label className="small">
                                      Archivo (.csv)
                                    </Label>
                                    <Input
                                      type="file"
                                      accept=".csv,text/csv"
                                      onChange={e =>
                                        setArchivoOficial(
                                          e.target.files[0] || null,
                                        )
                                      }
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={5}>
                                  <Button
                                    color="primary"
                                    disabled={!archivoOficial || importando}
                                    onClick={handleImportarRendimientoOficial}
                                  >
                                    {importando ? "Importando..." : "Importar"}
                                  </Button>
                                </Col>
                              </Row>
                            </CardBody>
                          </Card>
                        </TabPane>

                        <TabPane tabId="historial">
                          <h6 className="mb-2">Series cargadas</h6>
                          <DataTable
                            columns={columnasResumenOficial}
                            data={resumenOficial}
                            highlightOnHover
                            noDataComponent="Todavía no se importó ninguna serie"
                          />

                          <h6 className="mb-2 mt-4">Últimas importaciones</h6>
                          <DataTable
                            columns={columnasImportaciones}
                            data={importaciones}
                            pagination
                            paginationPerPage={5}
                            {...opcionesPaginacion(importaciones.length)}
                            paginationRowsPerPageOptions={[5, 10, 15, 20, 25]}
                            highlightOnHover
                            noDataComponent="Sin importaciones registradas"
                          />
                        </TabPane>
                      </TabContent>
                    </TabPane>

                    <TabPane tabId="configProyeccion">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                          <i className="mdi mdi-trending-up me-2 text-primary" />
                          Parámetros de Proyección Crediticia
                        </h5>
                        <Button
                          color="link"
                          size="sm"
                          onClick={async () => {
                            const data = await getConfigProyeccionLista()
                            setConfigProyeccion(data)
                            setSuccess("Parámetros recargados")
                          }}
                        >
                          <i className="mdi mdi-refresh me-1" />
                          Recargar
                        </Button>
                      </div>

                      {configProyeccion.length === 0 ? (
                        <Alert color="warning" fade={false}>
                          No hay parámetros de proyección configurados.
                        </Alert>
                      ) : (
                        <ConfigProyeccionTabs
                          configAgrupada={configAgrupada}
                          nombreCategoria={nombreCategoria}
                          formatearClave={formatearClave}
                          onConfigSaved={async () => {
                            const data = await getConfigProyeccionLista()
                            setConfigProyeccion(data)
                          }}
                          setSuccess={setSuccess}
                          setError={setError}
                        />
                      )}

                      <Alert
                        color="info"
                        className="mt-3 mb-0 py-2"
                        fade={false}
                      >
                        <small>
                          <i className="mdi mdi-information-outline me-1" />
                          Estos parámetros se usan en el cálculo de la
                          Proyección Crediticia del tab de Productor. Los
                          cambios aplican a proyecciones futuras; las ya
                          generadas mantienen los valores originales.
                        </small>
                      </Alert>
                    </TabPane>

                    {/* BDSJEA-136: Precios de Mercado deja de ser un item de
                        menu propio y pasa a ser una solapa mas de esta
                        pantalla. El panel se monta sin Layout: trae su propio
                        juego de sub-solapas (Rosario, Futuros, Bahia Blanca,
                        FOB, Hacienda, Regionales) y hace su propia carga. Se
                        renderiza solo cuando la solapa esta activa para no
                        pegarle a las seis fuentes al entrar a Administracion. */}
                    <TabPane tabId="preciosMercado">
                      {activeTab === "preciosMercado" && (
                        <PreciosMercadoPanel />
                      )}
                    </TabPane>

                    {/* BDSJEA-166: igual que Precios Mercado, se monta solo
                        cuando la solapa esta activa. El panel arranca un
                        intervalo de refresco si hay procesos corriendo y no
                        conviene tenerlo vivo de fondo. */}
                    <TabPane tabId="procesos">
                      {activeTab === "procesos" && <ProcesosPanel />}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>

              {/* Modal Campaña */}
              <Modal
                isOpen={modalCampana}
                toggle={() => setModalCampana(false)}
              >
                <ModalHeader toggle={() => setModalCampana(false)}>
                  {itemEdicion ? "Editar Campaña" : "Nueva Campaña"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion
                      ? {
                          codigo: itemEdicion.codigo || "",
                          descripcion: itemEdicion.descripcion || "",
                          fechaInicio: itemEdicion.fechaInicio || "",
                          fechaFin: itemEdicion.fechaFin || "",
                          activa: Boolean(itemEdicion.activa),
                        }
                      : {
                          codigo: "",
                          descripcion: "",
                          fechaInicio: "",
                          fechaFin: "",
                          activa: false,
                        }
                  }
                  validationSchema={getCampanaSchema(campanas, itemEdicion)}
                  onSubmit={handleGuardarCampana}
                  enableReinitialize
                >
                  {({
                    errors,
                    touched,
                    isSubmitting,
                    values,
                    setFieldValue,
                  }) => (
                    <Form>
                      <ModalBody>
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Código *</Label>
                              <Field name="codigo">
                                {({ field }) => (
                                  <input
                                    {...field}
                                    className="form-control"
                                    placeholder="2025/26"
                                    disabled={!!itemEdicion}
                                    onChange={e => {
                                      const val = e.target.value
                                      setFieldValue("codigo", val)
                                      if (
                                        CODIGO_CAMPANA_REGEX.test(val) &&
                                        !values.descripcion
                                      ) {
                                        const [yyyy, yy] = val.split("/")
                                        setFieldValue(
                                          "descripcion",
                                          `Campaña ${yyyy}/20${yy}`,
                                        )
                                      }
                                    }}
                                  />
                                )}
                              </Field>
                              {touched.codigo && errors.codigo && (
                                <div className="text-danger small">
                                  {errors.codigo}
                                </div>
                              )}
                              <small className="text-muted">
                                {MENSAJES_CAMPANA.ayuda}
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={8}>
                            <FormGroup>
                              <Label>Descripción</Label>
                              <Field
                                name="descripcion"
                                className="form-control"
                                placeholder="Ej: Campaña 2025/2026"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Fecha de inicio *</Label>
                              <Field
                                name="fechaInicio"
                                type="date"
                                className="form-control"
                              />
                              {touched.fechaInicio && errors.fechaInicio && (
                                <div className="text-danger small">
                                  {errors.fechaInicio}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Fecha de fin *</Label>
                              <Field
                                name="fechaFin"
                                type="date"
                                className="form-control"
                              />
                              {touched.fechaFin && errors.fechaFin && (
                                <div className="text-danger small">
                                  {errors.fechaFin}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup check className="mt-2">
                          <Label check>
                            <Field
                              type="checkbox"
                              name="activa"
                              className="form-check-input me-1"
                            />
                            Campaña activa (actual)
                          </Label>
                          <small className="d-block text-muted">
                            Al activar esta campaña se desactivará la anterior
                            automáticamente.
                          </small>
                        </FormGroup>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalCampana(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* MODALES - Se mantienen igual que tu componente original */}
              {/* Modal Zona */}
              <Modal isOpen={modalZona} toggle={() => setModalZona(false)}>
                <ModalHeader toggle={() => setModalZona(false)}>
                  {itemEdicion ? "Editar Zona" : "Nueva Zona"}
                </ModalHeader>
                <Formik
                  initialValues={
                    // BDSJEA-149: el bit puede llegar como 0/1 según el driver, y el
                    // checkbox de Formik necesita un booleano o queda siempre en
                    // falso al editar.
                    itemEdicion
                      ? {
                          ...itemEdicion,
                          activa: Boolean(itemEdicion.activa),
                          esReferenciaCalendario: Boolean(
                            itemEdicion.esReferenciaCalendario ??
                            itemEdicion.es_referencia_calendario,
                          ),
                        }
                      : {
                          nombre: "",
                          activa: true,
                          esReferenciaCalendario: false,
                        }
                  }
                  validationSchema={ZonaSchema}
                  onSubmit={handleGuardarZona}
                  enableReinitialize
                >
                  {({ errors, touched, isSubmitting }) => (
                    <Form>
                      <ModalBody>
                        <FormGroup>
                          <Label>Nombre de la Zona *</Label>
                          <Field
                            name="nombre"
                            className="form-control"
                            placeholder="Ej: Norte de Buenos Aires"
                          />
                          {touched.nombre && errors.nombre && (
                            <div className="text-danger small">
                              {errors.nombre}
                            </div>
                          )}
                        </FormGroup>
                        <FormGroup check className="mb-2">
                          <Label check>
                            <Field
                              type="checkbox"
                              name="esReferenciaCalendario"
                              className="form-check-input me-2"
                            />
                            Usar como referencia de calendario agrícola
                          </Label>
                          <div className="small text-muted">
                            Sus ventanas de siembra y cosecha se usan para los
                            establecimientos cuya zona no tiene calendario
                            propio, como Cuyo o Patagonia. Solo una zona puede
                            serlo: al marcar ésta se desmarca la anterior.
                          </div>
                        </FormGroup>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalZona(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* Modal Cultivo */}
              <Modal
                isOpen={modalCultivo}
                toggle={() => setModalCultivo(false)}
              >
                <ModalHeader toggle={() => setModalCultivo(false)}>
                  {itemEdicion ? "Editar Cultivo" : "Nuevo Cultivo"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion || {
                      nombre: "",
                      codigo: "",
                      tipo: "",
                      activo: true,
                    }
                  }
                  validationSchema={CultivoSchema}
                  onSubmit={handleGuardarCultivo}
                  enableReinitialize
                >
                  {({ errors, touched, isSubmitting }) => (
                    <Form>
                      <ModalBody>
                        <Row>
                          <Col md={5}>
                            <FormGroup>
                              <Label>Nombre *</Label>
                              <Field name="nombre" className="form-control" />
                              {touched.nombre && errors.nombre && (
                                <div className="text-danger small">
                                  {errors.nombre}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Código *</Label>
                              <Field name="codigo" className="form-control" />
                              {touched.codigo && errors.codigo && (
                                <div className="text-danger small">
                                  {errors.codigo}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Tipo</Label>
                              <Field
                                name="tipo"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Seleccione...</option>
                                <option value="CEREAL">Cereal</option>
                                <option value="OLEAGINOSA">Oleaginosa</option>
                                <option value="INDUSTRIAL">Industrial</option>
                                <option value="FORRAJERA">Forrajera</option>
                                {/* BDSJEA-157: tipos nuevos por el alta de
                                    legumbres y hortícolas del catálogo de MAGyP */}
                                <option value="LEGUMBRE">Legumbre</option>
                                <option value="HORTALIZA">Hortaliza</option>
                                {/* BDSJEA-158: plantaciones perennes */}
                                <option value="FRUTAL">Frutal</option>
                              </Field>
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>
                            Nombre en Estimaciones Agrícolas (MAGyP)
                          </Label>
                          <Field
                            name="nombreMagyp"
                            className="form-control"
                            placeholder="Ej: Soja 1ra"
                          />
                          <small className="text-muted">
                            Nombre exacto con el que el cultivo figura en el
                            archivo del Ministerio. Sin esto, la importación de
                            rendimiento oficial lo omite. No usar los totales de
                            la fuente (Soja total, Cebada total, Poroto total):
                            son sumas de sus componentes y duplicarían los
                            datos.
                          </small>
                        </FormGroup>
                        <div className="small text-muted">
                          Los días de ciclo se administran en Calendario
                          Agrícola, que los distingue por variante y por zona.
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalCultivo(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* Modal Concepto */}
              <Modal
                isOpen={modalConcepto}
                toggle={() => setModalConcepto(false)}
              >
                <ModalHeader toggle={() => setModalConcepto(false)}>
                  {itemEdicion ? "Editar Concepto" : "Nuevo Concepto"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion || { concepto: "", unidad: "", activo: true }
                  }
                  validationSchema={ConceptoSchema}
                  onSubmit={handleGuardarConcepto}
                  enableReinitialize
                >
                  {({ errors, touched, isSubmitting }) => (
                    <Form>
                      <ModalBody>
                        <FormGroup>
                          <Label>Concepto *</Label>
                          <Field name="concepto" className="form-control" />
                          {touched.concepto && errors.concepto && (
                            <div className="text-danger small">
                              {errors.concepto}
                            </div>
                          )}
                        </FormGroup>
                        <FormGroup>
                          <Label>Unidad *</Label>
                          <Field
                            name="unidad"
                            className="form-control"
                            placeholder="Ej: QQ/ha, USD/tn"
                          />
                          {touched.unidad && errors.unidad && (
                            <div className="text-danger small">
                              {errors.unidad}
                            </div>
                          )}
                        </FormGroup>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalConcepto(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* Modal Valor Agro */}
              <Modal
                isOpen={modalValorAgro}
                toggle={() => setModalValorAgro(false)}
                size="lg"
              >
                <ModalHeader toggle={() => setModalValorAgro(false)}>
                  {itemEdicion
                    ? "Editar Valores Agrícolas"
                    : "Nuevos Valores Agrícolas"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion || {
                      idZona: "",
                      idCultivo: "",
                      rendimiento: "",
                      precio: "",
                      gastosComercializacion: "",
                      arrendamientos: "",
                      costoDirecto: "",
                      administracionSiembra: "",
                      seguroGranizo: "",
                    }
                  }
                  validationSchema={ValorAgroSchema}
                  onSubmit={handleGuardarValorAgro}
                  enableReinitialize
                >
                  {({ errors, touched, isSubmitting, values }) => (
                    <Form>
                      <ModalBody>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Zona *</Label>
                              <Field
                                name="idZona"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Seleccione zona...</option>
                                {zonas.map(z => (
                                  <option key={z.id} value={z.id}>
                                    {z.nombre}
                                  </option>
                                ))}
                              </Field>
                              {touched.idZona && errors.idZona && (
                                <div className="text-danger small">
                                  {errors.idZona}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Cultivo *</Label>
                              <Field
                                name="idCultivo"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Seleccione cultivo...</option>
                                {cultivos
                                  .filter(c => c.activo !== false)
                                  .map(c => (
                                    <option key={c.id} value={c.id}>
                                      {c.nombre}
                                    </option>
                                  ))}
                              </Field>
                              {touched.idCultivo && errors.idCultivo && (
                                <div className="text-danger small">
                                  {errors.idCultivo}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Rendimiento (QQ/ha)</Label>
                              <Field
                                name="rendimiento"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Precio (USD/tn)</Label>
                              <Field
                                name="precio"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>G. Comercialización (USD/ha)</Label>
                              <Field
                                name="gastosComercializacion"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Arrendamientos</Label>
                              <Field
                                name="arrendamientos"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              {/* BDSJEA-76: el campo editable es el costo
                                  directo. "Costos Totales" era editable y no
                                  guardaba nada: costos_total es una columna
                                  calculada persistida, SQL Server no la deja
                                  escribir y el backend descarta ese valor del
                                  payload. */}
                              <Label>Costo Directo (USD/ha)</Label>
                              <Field
                                name="costoDirecto"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                              {/* BDSJEA-76: la aclaracion no es cosmetica. La
                                  definicion canonica de costo directo del
                                  sistema esta en CostoAgricola.calcularTotales()
                                  y AHI INCLUYE EL SEGURO. En valor_agro el
                                  seguro es una partida aparte que ya entra en la
                                  columna calculada costos_total, asi que cargar
                                  el costo directo con esa definicion lo cuenta
                                  dos veces. No es deducible del nombre del
                                  campo. */}
                              <small className="text-muted">
                                Labores, semilla, fertilizantes, agroquímicos y
                                cosecha. No incluye el seguro: se carga aparte.
                              </small>
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Admin. Siembra</Label>
                              <Field
                                name="administracionSiembra"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Seguro Granizo</Label>
                              <Field
                                name="seguroGranizo"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            {/* BDSJEA-76: solo lectura. Muestra en vivo la suma
                                de las cinco partidas mientras se edita, pero el
                                valor que vale es el que calcula la base. */}
                            <FormGroup>
                              <Label>Costos Totales (USD/ha)</Label>
                              <input
                                type="text"
                                className="form-control"
                                readOnly
                                tabIndex={-1}
                                value={formatearNumero(
                                  calcularCostosTotales(values),
                                  2,
                                )}
                              />
                              <small className="text-muted">
                                Suma de costo directo, gastos de
                                comercialización, arrendamientos, administración
                                de siembra y seguro. Lo calcula el sistema.
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalValorAgro(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* Modal Valor Ganadería */}
              <Modal
                isOpen={modalValorGanaderia}
                toggle={() => setModalValorGanaderia(false)}
              >
                <ModalHeader toggle={() => setModalValorGanaderia(false)}>
                  {itemEdicion
                    ? "Editar Valores Ganadería"
                    : "Nuevos Valores Ganadería"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion || {
                      codigoEspecie: "",
                      codigoCategoria: "",
                      pesoVenta: "",
                      precioKilo: "",
                      gastosVenta: "",
                      gastosProductivos: "",
                      gastosCria: "",
                    }
                  }
                  validationSchema={ValorGanaderiaSchema}
                  onSubmit={handleGuardarValorGanaderia}
                  enableReinitialize
                >
                  {({
                    errors,
                    touched,
                    isSubmitting,
                    values,
                    setFieldValue,
                  }) => (
                    <Form>
                      <ModalBody>
                        <fieldset
                          className="rounded p-3 mb-3"
                          style={{ border: "1px solid #ced4da" }}
                        >
                          <legend
                            className="w-auto px-2 float-none"
                            style={{
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              color: "#495057",
                            }}
                          >
                            Tipo Hacienda
                          </legend>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label>Especie *</Label>
                                {itemEdicion ? (
                                  <Input
                                    type="text"
                                    value={
                                      especiesGanaderas.find(
                                        e => e.codigo === values.codigoEspecie,
                                      )?.descripcion || values.codigoEspecie
                                    }
                                    disabled
                                  />
                                ) : (
                                  <Input
                                    type="select"
                                    name="codigoEspecie"
                                    value={values.codigoEspecie}
                                    onChange={e => {
                                      setFieldValue(
                                        "codigoEspecie",
                                        e.target.value,
                                      )
                                      setFieldValue("codigoCategoria", "")
                                    }}
                                  >
                                    <option value="">
                                      -- Seleccionar especie --
                                    </option>
                                    {especiesGanaderas.map(esp => (
                                      <option
                                        key={esp.codigo}
                                        value={esp.codigo}
                                      >
                                        {esp.descripcion}
                                      </option>
                                    ))}
                                  </Input>
                                )}
                                {touched.codigoEspecie &&
                                  errors.codigoEspecie && (
                                    <div className="text-danger small">
                                      {errors.codigoEspecie}
                                    </div>
                                  )}
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label>Categoría *</Label>
                                {itemEdicion ? (
                                  <Input
                                    type="text"
                                    value={
                                      categoriasGanaderas.find(
                                        c =>
                                          c.codigo === values.codigoCategoria,
                                      )?.descripcion || values.codigoCategoria
                                    }
                                    disabled
                                  />
                                ) : (
                                  <Input
                                    type="select"
                                    name="codigoCategoria"
                                    value={values.codigoCategoria}
                                    onChange={e =>
                                      setFieldValue(
                                        "codigoCategoria",
                                        e.target.value,
                                      )
                                    }
                                    disabled={!values.codigoEspecie}
                                  >
                                    <option value="">
                                      -- Seleccionar categoría --
                                    </option>
                                    {categoriasGanaderas
                                      .filter(
                                        c => c.especie === values.codigoEspecie,
                                      )
                                      .map(cat => (
                                        <option
                                          key={cat.codigo}
                                          value={cat.codigo}
                                        >
                                          {cat.descripcion}
                                        </option>
                                      ))}
                                  </Input>
                                )}
                                {values.codigoCategoria && (
                                  <small className="text-muted">
                                    {values.codigoCategoria}
                                    {" - "}
                                    {categoriasGanaderas.find(
                                      c => c.codigo === values.codigoCategoria,
                                    )?.descripcion || ""}
                                  </small>
                                )}
                                {touched.codigoCategoria &&
                                  errors.codigoCategoria && (
                                    <div className="text-danger small">
                                      {errors.codigoCategoria}
                                    </div>
                                  )}
                              </FormGroup>
                            </Col>
                          </Row>
                        </fieldset>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Peso Promedio (kg) *</Label>
                              <NumberInput
                                value={values.pesoVenta}
                                onChange={val =>
                                  setFieldValue("pesoVenta", val)
                                }
                                nroDecimales={2}
                                minDecimals={0}
                              />
                              {touched.pesoVenta && errors.pesoVenta && (
                                <div className="text-danger small">
                                  {errors.pesoVenta}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Precio/kg (USD) *</Label>
                              <AmountInput
                                value={values.precioKilo}
                                onChange={val =>
                                  setFieldValue("precioKilo", val)
                                }
                                nroDecimales={2}
                                minDecimals={0}
                                currency=""
                              />
                              {touched.precioKilo && errors.precioKilo && (
                                <div className="text-danger small">
                                  {errors.precioKilo}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Gastos Venta (%)</Label>
                              <NumberInput
                                value={values.gastosVenta}
                                onChange={val =>
                                  setFieldValue("gastosVenta", val)
                                }
                                nroDecimales={2}
                                minDecimals={0}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Gastos Productivos (USD)</Label>
                              <AmountInput
                                value={values.gastosProductivos}
                                onChange={val =>
                                  setFieldValue("gastosProductivos", val)
                                }
                                nroDecimales={2}
                                minDecimals={0}
                                currency=""
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Gastos Cría (USD)</Label>
                              <AmountInput
                                value={values.gastosCria}
                                onChange={val =>
                                  setFieldValue("gastosCria", val)
                                }
                                nroDecimales={2}
                                minDecimals={0}
                                currency=""
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalValorGanaderia(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* Modal Calendario Agrícola (BDSJEA-143) */}
              <Modal
                isOpen={modalCalendario}
                toggle={() => setModalCalendario(false)}
                size="lg"
              >
                <ModalHeader toggle={() => setModalCalendario(false)}>
                  {itemEdicion
                    ? "Editar Calendario Agrícola"
                    : "Nuevo Calendario Agrícola"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion || {
                      codigoCultivo: "",
                      variante: "",
                      idZona: "",
                      mesSiembraInicio: "",
                      mesSiembraFin: "",
                      mesCosechaInicio: "",
                      mesCosechaFin: "",
                      diasCicloMinimo: "",
                      diasCicloMaximo: "",
                      diasCicloTipico: "",
                      fuenteDatos: "",
                      activo: true,
                    }
                  }
                  validationSchema={CalendarioAgricolaSchema}
                  onSubmit={handleGuardarCalendario}
                  enableReinitialize
                >
                  {({ errors, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                      <ModalBody>
                        <Row>
                          <Col md={8}>
                            <FormGroup>
                              <Label>Cultivo *</Label>
                              <Field
                                name="codigoCultivo"
                                as="select"
                                className="form-control"
                                onChange={e => {
                                  const codigo = e.target.value
                                  setFieldValue("codigoCultivo", codigo)
                                }}
                              >
                                <option value="">Seleccione cultivo...</option>
                                {cultivos
                                  .filter(c => c.activo !== false)
                                  .map(c => (
                                    <option key={c.id} value={c.codigo}>
                                      {c.nombre} ({c.codigo})
                                    </option>
                                  ))}
                              </Field>
                              {touched.codigoCultivo &&
                                errors.codigoCultivo && (
                                  <div className="text-danger small">
                                    {errors.codigoCultivo}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Variante</Label>
                              <Field
                                name="variante"
                                className="form-control"
                                placeholder="1RA, TARDIO..."
                              />
                              <small className="text-muted">
                                Vacío = genérico
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Zona productiva *</Label>
                              <Field
                                name="idZona"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Seleccione zona...</option>
                                {zonas
                                  .filter(z => z.activa !== false)
                                  .map(z => (
                                    <option key={z.id} value={z.id}>
                                      {z.nombre}
                                    </option>
                                  ))}
                              </Field>
                              <small className="text-muted">
                                Las ventanas de siembra y cosecha no varían
                                dentro de la pampa húmeda, así que una misma
                                fecha puede repetirse en varias zonas.
                              </small>
                              {touched.idZona && errors.idZona && (
                                <div className="text-danger small">
                                  {errors.idZona}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Fuente de los datos</Label>
                              <Field
                                name="fuenteDatos"
                                className="form-control"
                                placeholder="INTA / BCR Rosario"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Siembra desde *</Label>
                              <Field
                                name="mesSiembraInicio"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Mes...</option>
                                {MESES_CALENDARIO.map(m => (
                                  <option key={m.valor} value={m.valor}>
                                    {m.nombre}
                                  </option>
                                ))}
                              </Field>
                              {touched.mesSiembraInicio &&
                                errors.mesSiembraInicio && (
                                  <div className="text-danger small">
                                    {errors.mesSiembraInicio}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Siembra hasta *</Label>
                              <Field
                                name="mesSiembraFin"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Mes...</option>
                                {MESES_CALENDARIO.map(m => (
                                  <option key={m.valor} value={m.valor}>
                                    {m.nombre}
                                  </option>
                                ))}
                              </Field>
                              {touched.mesSiembraFin &&
                                errors.mesSiembraFin && (
                                  <div className="text-danger small">
                                    {errors.mesSiembraFin}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Cosecha desde *</Label>
                              <Field
                                name="mesCosechaInicio"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Mes...</option>
                                {MESES_CALENDARIO.map(m => (
                                  <option key={m.valor} value={m.valor}>
                                    {m.nombre}
                                  </option>
                                ))}
                              </Field>
                              {touched.mesCosechaInicio &&
                                errors.mesCosechaInicio && (
                                  <div className="text-danger small">
                                    {errors.mesCosechaInicio}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>Cosecha hasta *</Label>
                              <Field
                                name="mesCosechaFin"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Mes...</option>
                                {MESES_CALENDARIO.map(m => (
                                  <option key={m.valor} value={m.valor}>
                                    {m.nombre}
                                  </option>
                                ))}
                              </Field>
                              {touched.mesCosechaFin &&
                                errors.mesCosechaFin && (
                                  <div className="text-danger small">
                                    {errors.mesCosechaFin}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Ciclo mínimo (días) *</Label>
                              <Field
                                name="diasCicloMinimo"
                                type="number"
                                className="form-control"
                                placeholder="140"
                              />
                              {touched.diasCicloMinimo &&
                                errors.diasCicloMinimo && (
                                  <div className="text-danger small">
                                    {errors.diasCicloMinimo}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Ciclo típico (días) *</Label>
                              <Field
                                name="diasCicloTipico"
                                type="number"
                                className="form-control"
                                placeholder="150"
                              />
                              {touched.diasCicloTipico &&
                                errors.diasCicloTipico && (
                                  <div className="text-danger small">
                                    {errors.diasCicloTipico}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label>Ciclo máximo (días) *</Label>
                              <Field
                                name="diasCicloMaximo"
                                type="number"
                                className="form-control"
                                placeholder="160"
                              />
                              {touched.diasCicloMaximo &&
                                errors.diasCicloMaximo && (
                                  <div className="text-danger small">
                                    {errors.diasCicloMaximo}
                                  </div>
                                )}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <div className="small text-muted mt-4">
                              El rendimiento y el costo de la zona se
                              administran en Valores Agro, no acá.
                            </div>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mt-4">
                              <Label check>
                                <Field
                                  type="checkbox"
                                  name="activo"
                                  className="form-check-input me-2"
                                />
                                Activo
                              </Label>
                            </FormGroup>
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalCalendario(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Guardando..." : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>

              {/* Modal Valor General */}
              <Modal
                isOpen={modalValorGeneral}
                toggle={() => setModalValorGeneral(false)}
              >
                <ModalHeader toggle={() => setModalValorGeneral(false)}>
                  {itemEdicion ? "Editar Valor General" : "Nuevo Valor General"}
                </ModalHeader>
                <Formik
                  initialValues={
                    itemEdicion || { concepto: "", valor: "", unidad: "" }
                  }
                  validationSchema={ValorGeneralSchema}
                  onSubmit={handleGuardarValorGeneral}
                  enableReinitialize
                >
                  {({ errors, touched, isSubmitting }) => (
                    <Form>
                      <ModalBody>
                        <FormGroup>
                          <Label>Concepto *</Label>
                          <Field
                            name="concepto"
                            className="form-control"
                            placeholder="Ej: % tambero"
                          />
                          {touched.concepto && errors.concepto && (
                            <div className="text-danger small">
                              {errors.concepto}
                            </div>
                          )}
                        </FormGroup>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Valor *</Label>
                              <Field
                                name="valor"
                                type="number"
                                step="0.01"
                                className="form-control"
                              />
                              {touched.valor && errors.valor && (
                                <div className="text-danger small">
                                  {errors.valor}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <Label>Unidad *</Label>
                              <Field
                                name="unidad"
                                as="select"
                                className="form-control"
                              >
                                <option value="">Seleccione...</option>
                                <option value="%">%</option>
                                <option value="USD">USD</option>
                                <option value="ARS">ARS</option>
                                <option value="kg">kg</option>
                                <option value="lt">lt</option>
                              </Field>
                              {touched.unidad && errors.unidad && (
                                <div className="text-danger small">
                                  {errors.unidad}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="secondary"
                          onClick={() => setModalValorGeneral(false)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? <Spinner size="sm" /> : "Guardar"}
                        </Button>
                      </ModalFooter>
                    </Form>
                  )}
                </Formik>
              </Modal>
            </>
          ),
        },
      ]}
    />
  )
}

// Sub-componente para headers de tabs
const TabHeader = ({ title, icon, onAdd }) => (
  <div className="d-flex justify-content-between align-items-center mb-3">
    <h5 className="mb-0">
      <i className={`mdi ${icon} me-2 text-primary`} />
      {title}
    </h5>
    <Button color="primary" size="sm" onClick={onAdd}>
      <i className="mdi mdi-plus me-1" />
      Agregar
    </Button>
  </div>
)

/**
 * BDSJEA-165 - Opciones cerradas de los parametros que no son numericos.
 *
 * La tabla agro.config_proyeccion guarda todo como VARCHAR y describe el tipo en
 * tipo_dato, pero no tiene forma de declarar un dominio de valores validos. Para
 * los pocos parametros que son una eleccion entre alternativas conocidas, el
 * dominio se define aca: escribir "MAGIP" a mano en un campo de texto libre
 * haria que la proyeccion cayera al default sin que nadie se entere.
 *
 * Si esto crece mas alla de un par de claves, corresponde una columna
 * valores_permitidos en la tabla y no seguir agrandando este mapa.
 */
const OPCIONES_CONFIG = {
  ORIGEN_RENDIMIENTO_PREFERIDO: [
    { valor: "MAGYP", etiqueta: "MAGYP — serie oficial" },
    { valor: "VALOR_AGRO", etiqueta: "VALOR_AGRO — parametría del banco" },
  ],
}

/**
 * BDSJEA-165 - Editor inline del valor de un parametro.
 *
 * Antes era siempre <Input type="number" step="0.01">, sin mirar tipo_dato. Con
 * eso, editar un parametro de texto mostraba un spinner numerico que no aceptaba
 * letras: el valor quedaba vacio y al guardar el backend lo rechazaba. Afectaba a
 * ORIGEN_RENDIMIENTO_PREFERIDO y al parametro de nombre de zona.
 */
const renderEditorValor = (
  p,
  editandoConfig,
  setEditandoConfig,
  handleGuardar,
) => {
  const onKeyDown = e => {
    if (e.key === "Enter") handleGuardar()
    if (e.key === "Escape") setEditandoConfig(null)
  }
  const onChange = e =>
    setEditandoConfig({ ...editandoConfig, valor: e.target.value })

  const opciones = OPCIONES_CONFIG[p.clave]
  if (opciones) {
    return (
      <Input
        bsSize="sm"
        type="select"
        value={editandoConfig.valor}
        onChange={onChange}
        onKeyDown={onKeyDown}
        style={{ width: "220px", display: "inline-block" }}
        autoFocus
      >
        {opciones.map(o => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
      </Input>
    )
  }

  const tipo = (p.tipoDato || p.tipo_dato || "").toUpperCase()
  const esNumerico =
    tipo === "" || tipo === "DECIMAL" || tipo === "ENTERO" || tipo === "INTEGER"

  if (!esNumerico) {
    return (
      <Input
        bsSize="sm"
        type="text"
        value={editandoConfig.valor}
        onChange={onChange}
        onKeyDown={onKeyDown}
        style={{ width: "220px", display: "inline-block" }}
        autoFocus
      />
    )
  }

  return (
    <Input
      bsSize="sm"
      type="number"
      step={tipo === "ENTERO" || tipo === "INTEGER" ? "1" : "0.01"}
      min={p.valorMinimo != null ? p.valorMinimo : p.valor_minimo}
      max={p.valorMaximo != null ? p.valorMaximo : p.valor_maximo}
      value={editandoConfig.valor}
      onChange={onChange}
      onKeyDown={onKeyDown}
      style={{ width: "90px", display: "inline-block" }}
      autoFocus
    />
  )
}

// Sub-componente para config proyección con sub-tabs por categoría
const ConfigProyeccionTabs = ({
  configAgrupada,
  nombreCategoria,
  formatearClave,
  onConfigSaved, // callback para recargar datos en el padre
  setSuccess,
  setError,
}) => {
  const categorias = Object.keys(configAgrupada)
  const [subTab, setSubTab] = useState(categorias[0] || "CAPACIDAD_PAGO")

  // ★ Estado de edición INTERNO (antes estaba en el padre)
  const [editandoConfig, setEditandoConfig] = useState(null)
  const [savingConfig, setSavingConfig] = useState(false)

  // ★ Handler de guardado INTERNO
  const handleGuardar = async () => {
    if (!editandoConfig) return
    setSavingConfig(true)
    try {
      await updateConfigProyeccion(editandoConfig.id, editandoConfig.valor)
      setSuccess("Parámetro actualizado")
      setEditandoConfig(null)
      // Notificar al padre para que recargue los datos
      if (onConfigSaved) await onConfigSaved()
    } catch (error) {
      setError(
        "Error al actualizar: " +
          (error?.response?.data?.error || error.message),
      )
    } finally {
      setSavingConfig(false)
    }
  }

  const iconCategoria = {
    CAPACIDAD_PAGO: "mdi-cash-multiple",
    NDVI: "mdi-satellite-variant",
    GANADERIA: "mdi-cow",
    TAMBO: "mdi-water",
    COSTOS: "mdi-currency-usd",
    DICTAMEN: "mdi-gavel",
  }

  const colorCategoria = {
    CAPACIDAD_PAGO: "#6f42c1",
    NDVI: "#198754",
    GANADERIA: "#8D6E63",
    TAMBO: "#0dcaf0",
    COSTOS: "#fd7e14",
    DICTAMEN: "#dc3545",
  }

  return (
    <>
      <Nav pills className="mb-3">
        {categorias.map(cat => (
          <NavItem key={cat}>
            <NavLink
              className={classnames({ active: subTab === cat })}
              onClick={() => {
                setSubTab(cat)
                setEditandoConfig(null)
              }}
              style={{ cursor: "pointer", fontSize: "0.85rem" }}
            >
              <i className={`mdi ${iconCategoria[cat] || "mdi-cog"} me-1`} />
              {nombreCategoria[cat] || cat}
              {/* <Badge pill className="ms-1" style={{ fontSize: "0.7rem" }}>
                {configAgrupada[cat]?.length || 0}
              </Badge> */}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {categorias.map(
        cat =>
          subTab === cat && (
            <div key={cat}>
              <div
                className="mb-2 text-muted"
                style={{
                  borderLeft: `3px solid ${colorCategoria[cat] || "#6c757d"}`,
                  paddingLeft: "12px",
                }}
              >
                <strong>{nombreCategoria[cat]}</strong>
                <small className="text-muted d-block">
                  {cat === "CAPACIDAD_PAGO" &&
                    "Porcentajes y ratios para determinar la capacidad de repago del productor."}
                  {cat === "NDVI" &&
                    "Factores de ajuste del rendimiento según el estado satelital del cultivo."}
                  {cat === "GANADERIA" &&
                    "Valores de referencia para valuación y proyección de ingresos ganaderos."}
                  {cat === "TAMBO" &&
                    "Parámetros de producción lechera para establecimientos tamberos."}
                  {cat === "COSTOS" &&
                    "Costos directos de producción por hectárea, por cultivo."}
                  {cat === "DICTAMEN" &&
                    "Configuración de plazos y umbrales para el dictamen crediticio."}
                </small>
              </div>

              <Table size="sm" hover bordered className="mt-2 mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "28%" }}>Parámetro</th>
                    <th style={{ width: "10%" }} className="text-center">
                      Valor
                    </th>
                    <th style={{ width: "8%" }}>Unidad</th>
                    <th style={{ width: "7%" }} className="text-center">
                      Mín
                    </th>
                    <th style={{ width: "7%" }} className="text-center">
                      Máx
                    </th>
                    <th style={{ width: "30%" }}>Descripción</th>
                    <th style={{ width: "10%" }} className="text-center">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(configAgrupada[cat] || []).map(p => (
                    <tr key={p.id}>
                      <td>
                        <small>{formatearClave(p.clave)}</small>
                      </td>
                      <td className="text-center">
                        {editandoConfig && editandoConfig.id === p.id ? (
                          renderEditorValor(
                            p,
                            editandoConfig,
                            setEditandoConfig,
                            handleGuardar,
                          )
                        ) : (
                          <strong>{p.valor}</strong>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">{p.unidad}</small>
                      </td>
                      <td className="text-center">
                        <small className="text-muted">
                          {p.valor_minimo || p.valorMinimo}
                        </small>
                      </td>
                      <td className="text-center">
                        <small className="text-muted">
                          {p.valor_maximo || p.valorMaximo}
                        </small>
                      </td>
                      <td>
                        <small className="text-muted">{p.descripcion}</small>
                      </td>
                      <td className="text-center">
                        {editandoConfig && editandoConfig.id === p.id ? (
                          <div className="d-flex gap-1 justify-content-center">
                            <Button
                              color="success"
                              size="sm"
                              onClick={handleGuardar}
                              disabled={savingConfig}
                            >
                              {savingConfig ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="mdi mdi-check" />
                              )}
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              onClick={() => setEditandoConfig(null)}
                            >
                              <i className="mdi mdi-close" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            color="link"
                            size="sm"
                            onClick={() =>
                              // BDSJEA-165: se guarda tambien la clave para que
                              // el editor sepa que control renderizar.
                              setEditandoConfig({
                                id: p.id,
                                clave: p.clave,
                                valor: p.valor,
                              })
                            }
                          >
                            <i className="mdi mdi-pencil" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ),
      )}
    </>
  )
}

export default AdministracionAgropecuario
